import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, Portal, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ConfigurationSupportApi from "../apis/configurationSupportApi";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { isSupportAgentLdEnabled } from "../utils/supportAgentAccess";
import { ConfigurationSupportMessage } from "./types";
import { useConfigurationSupportSnapshot } from "./ConfigurationSupportContext";
import { renderSupportMarkdown } from "./renderSupportMarkdown";

const SUGGESTIONS: Record<string, string[]> = {
  integrations: ["Why can't I save this?", "What does the test error mean?"],
  platform_configuration: ["What is still missing on this platform?", "Why did the connection test fail?"],
  application_configuration: ["Why is Save disabled?", "What should I fix before testing again?"],
  custom_workflow: ["Which steps should stay enabled?", "Will this workflow run for the customer I selected?"],
};

const ConfigurationSupportChat: React.FC = () => {
  const snapshot = useConfigurationSupportSnapshot();
  const supportAgentEnabled = isSupportAgentLdEnabled(useFeatureFlag() as Record<string, unknown>);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [messages, setMessages] = useState<ConfigurationSupportMessage[]>([]);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const contextKey = snapshot ? `${snapshot.surface}:${snapshot.title}` : "";

  useEffect(() => {
    setMessages([]);
    setError("");
    setDraft("");
  }, [contextKey]);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages, sending, open]);

  const suggestions = useMemo(
    () => (snapshot ? SUGGESTIONS[snapshot.surface] || [] : []),
    [snapshot]
  );

  if (!supportAgentEnabled || !snapshot) return null;

  const send = async (question: string) => {
    const trimmed = question.trim();
    if (!trimmed || sending) return;
    const history = messages.slice(-8);
    const nextMessages: ConfigurationSupportMessage[] = [...messages, { role: "user", content: trimmed }];
    setMessages(nextMessages);
    setDraft("");
    setError("");
    setSending(true);
    try {
      const response = await new ConfigurationSupportApi().ask({ snapshot, question: trimmed, history });
      const answer = String(response?.answer || "").trim();
      if (!answer) throw new Error("Theo did not return an answer.");
      setMessages((current) => [...current, { role: "assistant", content: answer }]);
    } catch (requestError: any) {
      setError(requestError?.message || "Configuration help is unavailable right now.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Portal>
      <Box sx={{ position: "fixed", right: 24, bottom: 24, zIndex: 1600, fontFamily: "var(--theo-font-sans)" }}>
        {open ? (
          <Box
            sx={{
              width: { xs: "calc(100vw - 32px)", sm: 400 },
              height: 540,
              maxHeight: "calc(100vh - 48px)",
              display: "flex",
              flexDirection: "column",
              background: "#FFFFFF",
              borderRadius: "10px",
              boxShadow: "0 16px 40px rgba(28, 25, 23, 0.18)",
              border: "1px solid #E7E1D8",
              overflow: "hidden",
            }}
          >
            <Box sx={{ px: 2, py: 1.5, background: "#705D4A", color: "#FFF" }}>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                <Box>
                  <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700, fontSize: 16 }}>
                    Configuration help
                  </Typography>
                  <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, opacity: 0.9 }}>
                    {snapshot.title}
                  </Typography>
                </Box>
                <IconButton aria-label="Close configuration help" onClick={() => setOpen(false)} sx={{ color: "#FFF" }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Box ref={threadRef} sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5, background: "#F8FAFC" }}>
              {messages.length === 0 ? (
                <Box>
                  <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 14, color: "#3F3A36", mb: 1.5 }}>
                    Theo can see the configuration on this page, including fields you have filled in. Secrets stay in the form.
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    {suggestions.map((suggestion) => (
                      <Box
                        key={suggestion}
                        component="button"
                        type="button"
                        onClick={() => send(suggestion)}
                        sx={{
                          textAlign: "left",
                          border: "1px solid #E7E1D8",
                          background: "#FFF",
                          borderRadius: "6px",
                          px: 1.5,
                          py: 1,
                          cursor: "pointer",
                          fontFamily: "var(--theo-font-sans)",
                          fontSize: 14,
                          color: "#3F3A36",
                        }}
                      >
                        {suggestion}
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                messages.map((message, index) => (
                  <Box
                    key={`${message.role}-${index}`}
                    sx={{
                      mb: 1,
                      ml: message.role === "user" ? 4 : 0,
                      mr: message.role === "assistant" ? 2 : 0,
                      px: 1.5,
                      py: 1,
                      borderRadius: "8px",
                      background: message.role === "user" ? "#705D4A" : "#FFFFFF",
                      color: message.role === "user" ? "#FFF" : "#1F1B16",
                      border: message.role === "assistant" ? "1px solid #E7E1D8" : "none",
                      whiteSpace: message.role === "user" ? "pre-wrap" : "normal",
                      fontFamily: "var(--theo-font-sans)",
                      fontSize: 14,
                      lineHeight: 1.45,
                      "& p": { margin: 0 },
                      "& p + p": { marginTop: "8px" },
                      "& ol, & ul": { margin: "8px 0 0", paddingLeft: "1.25rem" },
                      "& li + li": { marginTop: "6px" },
                      "& strong, & b": { fontWeight: 700 },
                    }}
                    {...(message.role === "assistant"
                      ? { dangerouslySetInnerHTML: { __html: renderSupportMarkdown(message.content) } }
                      : { children: message.content })}
                  />
                ))
              )}
              {sending ? (
                <Box display="flex" alignItems="center" gap={1} sx={{ color: "#666F8D", mt: 1 }}>
                  <CircularProgress size={16} sx={{ color: "#705D4A" }} />
                  <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13 }}>Looking at this configuration…</Typography>
                </Box>
              ) : null}
              {error ? (
                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#B42318", mt: 1 }}>{error}</Typography>
              ) : null}
            </Box>
            <Box
              component="form"
              onSubmit={(event) => {
                event.preventDefault();
                send(draft);
              }}
              sx={{ display: "flex", gap: 1, p: 1.5, borderTop: "1px solid #E7E1D8", background: "#FFF" }}
            >
              <TextField
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="Ask about this configuration"
                size="small"
                fullWidth
                disabled={sending}
                inputProps={{ "aria-label": "Configuration question" }}
              />
              <Box
                component="button"
                type="submit"
                disabled={sending || !draft.trim()}
                sx={{
                  border: 0,
                  borderRadius: "6px",
                  px: 1.5,
                  background: "#705D4A",
                  color: "#FFF",
                  fontFamily: "var(--theo-font-sans)",
                  fontWeight: 600,
                  cursor: "pointer",
                  "&:disabled": { opacity: 0.5, cursor: "default" },
                }}
              >
                Send
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            component="button"
            type="button"
            onClick={() => setOpen(true)}
            sx={{
              border: 0,
              borderRadius: "999px",
              px: 2,
              py: 1.25,
              background: "#705D4A",
              color: "#FFF",
              boxShadow: "0 8px 24px rgba(112, 93, 74, 0.35)",
              cursor: "pointer",
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            Ask about this setup
          </Box>
        )}
      </Box>
    </Portal>
  );
};

export default ConfigurationSupportChat;
